import type {
  IntegrationStreamName,
  IntegrationStreamView,
  IntegrationSubscriptionView,
  RunStreamsResult,
} from '@vagusx/clients';

/**
 * What the Sync dialog shows and decides (ADR-299 §3.5), kept apart from the
 * component so it can be tested without rendering.
 */

export const STREAM_LABELS: Record<IntegrationStreamName, { title: string; detail: string }> = {
  catalog: { title: 'Catalog', detail: 'Providers, departments and appointment types' },
  schedule_load: {
    title: 'Reload schedule',
    detail: 'Every open slot (next 180 days) and booked appointment (180 days either side)',
  },
  schedule_edge: {
    title: 'Weekly slot re-read',
    detail: 'Every open slot again; drops slots deleted or retyped in the EHR',
  },
  appointment_feed: { title: 'Appointment changes', detail: "The EHR's appointment change feed" },
  patient_feed: { title: 'Patient changes', detail: "The EHR's patient change feed" },
  waitlist_push: { title: 'Waitlist: send', detail: 'Pending waitlist entries to the EHR' },
  waitlist_pull: { title: 'Waitlist: refresh', detail: "The EHR's waitlist" },
};

/**
 * Streams ticked when the dialog opens: the cheap ones. Anything that re-reads
 * open slots or booked appointments in bulk starts unticked, so a Sync never
 * fans out into thousands of EHR calls by default (the 2026-09-29 demo).
 */
const EXPENSIVE: ReadonlySet<IntegrationStreamName> = new Set(['schedule_load', 'schedule_edge']);

export function defaultSelection(streams: IntegrationStreamView[]): Set<IntegrationStreamName> {
  return new Set(
    streams
      .filter((s) => s.enabled && s.status !== 'running' && !EXPENSIVE.has(s.stream))
      .map((s) => s.stream)
  );
}

/** Why a stream can't be ticked right now, or null when it can. */
export function unavailableReason(stream: IntegrationStreamView): string | null {
  if (stream.status === 'running') return 'Running now';
  if (!stream.enabled) return 'Paused';
  return null;
}

export type Tone = 'ok' | 'warn' | 'error' | 'muted';

export const STREAM_STATUS_BADGE: Record<
  IntegrationStreamView['status'],
  { label: string; tone: Tone }
> = {
  running: { label: 'Running', tone: 'muted' },
  paused: { label: 'Paused', tone: 'muted' },
  failed: { label: 'Failed', tone: 'error' },
  ok: { label: 'OK', tone: 'ok' },
  never_run: { label: 'Not run yet', tone: 'muted' },
};

export const FEED_LABELS: Record<IntegrationSubscriptionView['feed'], string> = {
  appointments: 'Appointments',
  patients: 'Patients',
};

/** The subscription's badge: what the EHR reports, or that a change is still landing. */
export function subscriptionBadge(sub: IntegrationSubscriptionView): { label: string; tone: Tone } {
  if (sub.state === 'pending' && sub.error) {
    // The EHR refused the last request; the next attempt waits out the pending window.
    return { label: 'Request failed, retrying', tone: 'error' };
  }
  if (sub.state === 'pending') {
    return {
      label: sub.requested === 'unsubscribe' ? 'Unsubscribing…' : 'Subscribing…',
      tone: 'warn',
    };
  }
  if (sub.error && sub.state !== 'converged') return { label: 'Check failed', tone: 'error' };
  switch (sub.observed) {
    case 'active':
      return { label: 'Active', tone: 'ok' };
    case 'partial':
      return { label: 'Partial', tone: 'warn' };
    case 'inactive':
      return { label: 'Not subscribed', tone: sub.desired === 'subscribed' ? 'error' : 'muted' };
    case 'unknown':
      return { label: 'Unknown', tone: 'warn' };
    default:
      return { label: 'Not checked', tone: 'muted' };
  }
}

/** The API would refuse a flip right now (409): the last request may still be landing. */
function isChangeLocked(sub: IntegrationSubscriptionView, now: Date): boolean {
  return sub.changeLockedUntil !== null && new Date(sub.changeLockedUntil) > now;
}

/**
 * The one action the row offers: subscribe when we don't want it on, unsubscribe
 * when we do. Disabled for as long as the API would refuse it — while the last
 * request may still be landing, even once the EHR shows it applied — because a
 * second request can land before the first (ADR-299 Gotcha 14).
 */
export function subscriptionAction(
  sub: IntegrationSubscriptionView,
  now: Date = new Date()
): {
  desired: 'subscribed' | 'unsubscribed';
  label: string;
  disabled: boolean;
} {
  const next = sub.desired === 'subscribed' ? 'unsubscribed' : 'subscribed';
  return {
    desired: next,
    label: next === 'subscribed' ? 'Subscribe' : 'Unsubscribe',
    disabled: sub.state === 'pending' || isChangeLocked(sub, now),
  };
}

/**
 * Keep polling while anything is still moving: a run in progress, a change landing,
 * or a locked subscription button that should unlock on its own.
 */
export function shouldPoll(
  streams: IntegrationStreamView[] | undefined,
  subscriptions: IntegrationSubscriptionView[] | undefined,
  now: Date = new Date()
): boolean {
  return (
    (streams ?? []).some((s) => s.status === 'running') ||
    (subscriptions ?? []).some((s) => s.state === 'pending' || isChangeLocked(s, now))
  );
}

/** A toast for the run request: what started, and why anything didn't. */
export function describeRunResults(result: RunStreamsResult): {
  kind: 'success' | 'warning' | 'error';
  title: string;
  description?: string;
} {
  const started = result.results
    .filter((r) => r.accepted)
    .map((r) => STREAM_LABELS[r.stream].title);
  const refused = result.results
    .filter((r) => !r.accepted)
    .map((r) => `${STREAM_LABELS[r.stream].title}: ${r.reason ?? 'not started'}`);
  if (started.length === 0) {
    return { kind: 'error', title: 'Nothing started', description: refused.join(' · ') };
  }
  const title = `Started: ${started.join(', ')}`;
  if (refused.length === 0) return { kind: 'success', title, description: 'Runs within a minute.' };
  return { kind: 'warning', title, description: refused.join(' · ') };
}
